import { useMemo } from 'react';

import { CreatePlanRequest } from '../types/plan.types';
import { labelToEnum, parsePlacingMethodFromCell } from '../services/excel-table.service';
import { getCompanyIdByName } from '../components/materials/autocomplete-options';
import { defaultPlanValue } from '../stores/create-plan-table.store';
import { parseDateFromYYMMDD } from '../components/materials/datepickers/datepicker-in-excel';
import { planToTableRow } from '../utils/plan-field-converters';
import { convertToHalf } from '../components/common/component-editor-class';

import type { SearchOption } from '@/types/search';
import type {
  CellChange,
  PlanTableData,
  PlanTableRow,
  RowsWithErrors,
} from '../components/common/handsontable.types';

import { TYPE_BOOLEAN, PLAN_FIELDS } from '@/lib/constants';

type StringableRow = (string | number | boolean | null | undefined)[];
const asStringableRow = (row: PlanTableRow): StringableRow => row as StringableRow;
const toCellNameArg = (value: string | number | boolean | null | undefined): string | undefined =>
  value == null ? undefined : String(value);
const toCellString = (value: string | number | boolean | null | undefined): string =>
  value == null ? '' : String(value);

export const getDefaultPlanData = (
  _index: number,
  plan: CreatePlanRequest,
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = [],
  isDetail: boolean = false,
  canUpdate: boolean = true
): PlanTableRow => {
  // Use shared conversion utility
  const data = planToTableRow(plan, plantOptions, witnessCompanyOptions, canUpdate);

  if (isDetail) {
    data.push(plan.id ?? '');
    data.push(plan.isDeleted ? TYPE_BOOLEAN.TRUE : TYPE_BOOLEAN.FALSE);
    data.push(plan.isArchive ? TYPE_BOOLEAN.TRUE : TYPE_BOOLEAN.FALSE);
  }
  return data;
};

/**
 * Shared base fields when converting a table row to plan (used by convertFromTableItem and convertFromTableItemPreserveString).
 * Resolves company IDs and returns orderStatus through placeToPlace to avoid duplication.
 */
const getRowToPlanBaseFields = (
  row: PlanTableRow,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[]
) => {
  const cells = asStringableRow(row);
  const customerSupplierIdStr = getCompanyIdByName(toCellNameArg(cells[1]), plantOptions); // ブランド (1)
  const witnessCompanyIdStr = getCompanyIdByName(toCellNameArg(cells[18]), witnessCompanyOptions); // その他 (18) - Witness Company autocomplete
  return {
    orderStatus: labelToEnum.orderStatus(toCellString(cells[0])), // ステータス (0)
    customerSupplierId: customerSupplierIdStr || null, // ブランド (1)
    placementDate: parseDateFromYYMMDD(toCellNameArg(cells[2])), // 打設日 (2)
    timeHope: cells[3] ? toCellString(cells[3]) : '', // 希望 (3)
    timePossible: cells[4] ? toCellString(cells[4]) : '', // 可能 (4)
    timePossibleRemarks: cells[5] ? toCellString(cells[5]) : null, // 可能時間補助 (5)
    placeToPlace: toCellString(cells[6]), // 打設箇所 (6)
    witnessCompanyIdStr,
  };
};

export const convertFromTableItem = (
  row: PlanTableRow,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[]
) => {
  const cells = asStringableRow(row);
  const { placingMethod, placingMethodOthers } = parsePlacingMethodFromCell(
    toCellNameArg(cells[17])
  ); // 打設手法 (17)

  const base = getRowToPlanBaseFields(row, plantOptions, witnessCompanyOptions);
  const { witnessCompanyIdStr, ...baseFields } = base;

  return {
    ...baseFields,
    // Convert to number when submitting - same logic as precedingMaterialQuantity but quantity is required (default to 0)
    quantity: cells[7] === '' || cells[7] == null ? 0 : Number(cells[7]) || 0, // 数量 (7)
    quantitySuffix: labelToEnum.quantitySuffix(toCellString(cells[8])), // 接尾辞 (8)
    quantityUnit: cells[9] ? toCellString(cells[9]) : null, // 単位 (9)
    formulationProductType: labelToEnum.formulationProductType(toCellString(cells[10])), // 種類 (10)
    formulationProduct: toCellString(cells[11]), // 内容 (11)
    vehicleSize: labelToEnum.vehicleSize(toCellString(cells[12])), // 車両 (12)
    precedingMaterialType: labelToEnum.materialType(toCellString(cells[13])), // 種類 (13)
    precedingMaterialQuantity: cells[14] ? Number(cells[14]) : null, // 数量 (14)
    precedingMaterialUnit: null,
    pitchHope: cells[15] ? toCellString(cells[15]) : null, // 希望 (15)
    pitchPossible: cells[16] ? toCellString(cells[16]) : null, // 可能 (16)
    placingMethod, // 打設手法 (17)
    placingMethodOthers, // free text when not ポンプ1基/2基/バケット/ネコ
    witnessCompanyId: witnessCompanyIdStr || null, // その他 (18) - Convert witness company name to witnessCompanyId (UUID string)
    witnessName: toCellString(cells[19]), // 氏名 (19)
    witnessTel: toCellString(cells[20]), // 連絡先 (20)
    remarks: cells[21] ? toCellString(cells[21]) : null, // 社内 (21)
    remarksForClient: cells[22] ? toCellString(cells[22]) : null, // ゼネコン (22)
    uuid: null,
    isArchive: false,
  };
};

/**
 * Convert CreatePlanRequest array to Handsontable data format
 * Converts enum values to display labels for dropdown columns
 * Converts company IDs to names for autocomplete columns
 */
export const convertToTableData = (
  plans: CreatePlanRequest[],
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = [],
  isDetail: boolean = false,
  canUpdate: boolean = true
): PlanTableData => {
  const result: PlanTableData = [];
  for (let index = 0; index < plans.length; index++) {
    result.push(
      getDefaultPlanData(
        index,
        plans[index],
        plantOptions,
        witnessCompanyOptions,
        isDetail,
        canUpdate
      )
    );
  }
  return result;
};

/**
 * Convert table item preserving string values (for layout switching)
 * Preserves string values to prevent NaN conversion during layout switching
 */
export const convertFromTableItemPreserveString = (
  row: PlanTableRow,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[]
) => {
  const cells = asStringableRow(row);
  const base = getRowToPlanBaseFields(row, plantOptions, witnessCompanyOptions);
  const { witnessCompanyIdStr, ...baseFields } = base;
  const { placingMethod, placingMethodOthers } = parsePlacingMethodFromCell(
    toCellNameArg(cells[17])
  );

  const plantCellText =
    cells[1] != null && String(cells[1]).trim() !== '' ? String(cells[1]).trim() : '';
  const witnessCellText =
    cells[18] != null && String(cells[18]).trim() !== '' ? String(cells[18]).trim() : '';

  const resolvedPlantId = baseFields.customerSupplierId;
  const resolvedWitnessId = witnessCompanyIdStr || null;

  // When the cell text does not match any option, IDs stay null but the UI still shows the typed
  // string. planToTableRow only shows that text via customerSupplier / witnessCompany fallbacks;
  // without these stubs, 1-line → 3-line conversion would clear プラント / 立会会社 cells.
  const customerSupplierStub =
    !resolvedPlantId && plantCellText
      ? ({ id: '', abbreviationName: plantCellText } as CreatePlanRequest['customerSupplier'])
      : undefined;

  const witnessCompanyStub =
    !resolvedWitnessId && witnessCellText
      ? ({ id: '', name: witnessCellText } as CreatePlanRequest['witnessCompany'])
      : undefined;

  return {
    ...baseFields,
    ...(customerSupplierStub ? { customerSupplier: customerSupplierStub } : {}),
    // Preserve string like precedingMaterialQuantity - will be converted to number when submitting
    quantity: (cells[7] === '' || cells[7] == null ? '' : cells[7]) as unknown as number, // 数量 (7)
    quantitySuffix: labelToEnum.quantitySuffix(toCellString(cells[8])), // 接尾辞 (8)
    quantityUnit: cells[9] ? toCellString(cells[9]) : null, // 単位 (9)
    formulationProductType: labelToEnum.formulationProductType(toCellString(cells[10])), // 種類 (10)
    formulationProduct: toCellString(cells[11]), // 内容 (11)
    vehicleSize: labelToEnum.vehicleSize(toCellString(cells[12])), // 車両 (12)
    precedingMaterialType: labelToEnum.materialType(toCellString(cells[13])), // 種類 (13)
    precedingMaterialQuantity:
      cells[14] == null || cells[14] === '' ? null : toCellString(cells[14]), // 数量 (14) - Preserve string
    precedingMaterialUnit: null,
    pitchHope: cells[15] ? toCellString(cells[15]) : null, // 希望 (15)
    pitchPossible: cells[16] ? toCellString(cells[16]) : null, // 可能 (16)
    placingMethod,
    placingMethodOthers,
    witnessCompanyId: resolvedWitnessId, // その他 (18) - Convert witness company name to witnessCompanyId (UUID string)
    ...(witnessCompanyStub ? { witnessCompany: witnessCompanyStub } : {}),
    witnessName: toCellString(cells[19]), // 氏名 (19)
    witnessTel: toCellString(cells[20]), // 連絡先 (20)
    remarks: cells[21] ? toCellString(cells[21]) : null, // 社内 (21)
    remarksForClient: cells[22] ? toCellString(cells[22]) : null, // ゼネコン (22)
    uuid: null,
    isArchive: false,
  };
};

/**
 * Convert Handsontable data format back to CreatePlanRequest array preserving string values
 * Used when switching layouts to prevent NaN conversion
 */
export const convertFromTableDataPreserveString = (
  tableData: PlanTableData,
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = []
): CreatePlanRequest[] => {
  const result: CreatePlanRequest[] = [];
  for (let i = 0; i < tableData.length; i++) {
    result.push(
      convertFromTableItemPreserveString(tableData[i], plantOptions, witnessCompanyOptions)
    );
  }
  return result;
};

/**
 * Convert Handsontable data format back to CreatePlanRequest array
 * Converts display labels back to enum values for dropdown columns
 * Converts company names back to IDs for autocomplete columns
 */
export const convertFromTableData = (
  tableData: PlanTableData,
  plantOptions: SearchOption[] = [],
  witnessCompanyOptions: SearchOption[] = []
): CreatePlanRequest[] => {
  const result: CreatePlanRequest[] = [];
  for (let i = 0; i < tableData.length; i++) {
    result.push(convertFromTableItem(tableData[i], plantOptions, witnessCompanyOptions));
  }
  return result;
};

/**
 * Hook to convert plans to table data format
 */
export const useExcelTable = (plans: CreatePlanRequest[]) => {
  const tableData = useMemo(() => convertToTableData(plans), [plans]);

  return {
    tableData,
    convertToTableData,
    convertFromTableData,
  };
};

// Map column index to PLAN_FIELDS key
// Column index corresponds to field id (starting from 1, but column index starts from 0)
// PLAN_FIELDS.id starts from 1, so we need to add 1 to columnIndex
const getFieldByColumnIndex = (columnIndex: number) => {
  const fieldId = columnIndex + 1; // PLAN_FIELDS.id starts from 1
  const fieldEntry = Object.entries(PLAN_FIELDS).find(([_, field]) => field.id === fieldId);
  return fieldEntry ? PLAN_FIELDS[fieldEntry[0] as keyof typeof PLAN_FIELDS] : null;
};

export const validateRow = (value: unknown, index: number): string | null => {
  const planField = getFieldByColumnIndex(index);
  if (!planField || !('validate' in planField) || typeof planField.validate !== 'function')
    return null; // No validation for this column
  const isValid = planField.validate(value);
  if (isValid) return null;
  // Check if message property exists before accessing it
  return 'message' in planField ? planField.message : null;
};

/**
 * Validate plant option value
 */
const validatePlantOption = (
  columnIndex: number,
  value: unknown,
  plantOptions: SearchOption[],
  row?: PlanTableRow
): string | null => {
  const ID_COLUMN_INDEX = PLAN_FIELDS.PLAN_ID.id - 1;
  const isPlantColumn = columnIndex === PLAN_FIELDS.PLANT_ID.id - 1;
  if (!isPlantColumn) return null;

  const normalizedValue = value != null ? String(value).trim().toLowerCase() : '';

  const hasExistingRowId =
    Array.isArray(row) && row.length > ID_COLUMN_INDEX && String(row[ID_COLUMN_INDEX] ?? '') !== '';
  if (hasExistingRowId && normalizedValue !== '') {
    // Existing records can keep plant values that are no longer in latest option list.
    return null;
  }

  const isValidOption = plantOptions.some(
    (opt) =>
      String(opt?.label ?? '')
        .trim()
        .toLowerCase() === normalizedValue
  );
  return isValidOption ? null : PLAN_FIELDS.PLANT_ID.message;
};

/**
 * Validate witness company option value
 */
const validateWitnessCompanyOption = (
  columnIndex: number,
  value: unknown,
  witnessCompanyOptions: SearchOption[],
  row?: PlanTableRow
): string | null => {
  const ID_COLUMN_INDEX = PLAN_FIELDS.PLAN_ID.id - 1;
  const isWitnessCompanyColumn = columnIndex === PLAN_FIELDS.WITNESS_COMPANY_ID.id - 1;
  if (!isWitnessCompanyColumn) return null;

  const normalizedValue = value != null ? String(value).trim().toLowerCase() : '';
  if (!normalizedValue) return null;

  const hasExistingRowId =
    Array.isArray(row) && row.length > ID_COLUMN_INDEX && String(row[ID_COLUMN_INDEX] ?? '') !== '';
  if (hasExistingRowId) {
    // Existing records can keep witness values that are no longer in latest option list.
    return null;
  }

  const isValidOption = witnessCompanyOptions.some(
    (opt) =>
      String(opt?.label ?? '')
        .trim()
        .toLowerCase() === normalizedValue
  );
  return isValidOption ? null : PLAN_FIELDS.WITNESS_COMPANY_ID.message;
};

/**
 * Validate one cell by combining generic field validation and option-list validation
 * (プラント / 立会会社).
 */
export const validateCellError = (
  columnIndex: number,
  value: unknown,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[],
  row?: PlanTableRow
): string | null => {
  const baseError = validateRow(value, columnIndex);
  if (baseError) return baseError;
  return (
    validatePlantOption(columnIndex, value, plantOptions, row) ||
    validateWitnessCompanyOption(columnIndex, value, witnessCompanyOptions, row)
  );
};

/**
 * Validate a single row and return field errors
 */
const validateRowFields = (
  row: PlanTableRow,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[]
): { index: number; message: string }[] => {
  const fieldErrors: { index: number; message: string }[] = [];

  for (let columnIndex = 0; columnIndex < row.length; columnIndex++) {
    const value = row[columnIndex];
    const errorMessage = validateCellError(
      columnIndex,
      value,
      plantOptions,
      witnessCompanyOptions,
      row
    );

    if (errorMessage) {
      fieldErrors.push({
        index: columnIndex,
        message: errorMessage,
      });
    }
  }

  return fieldErrors;
};

/**
 * Check if row is different from default plan data
 */
const isRowDifferentFromDefault = (
  row: PlanTableRow,
  rowIndex: number,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[]
): boolean => {
  const defaultRow = getDefaultPlanData(
    rowIndex,
    defaultPlanValue,
    plantOptions,
    witnessCompanyOptions
  );
  // Detail/Edit rows can include metadata columns: [id, isDeleted, isArchive]
  // Validation should compare only editable columns so untouched blank rows are skipped.
  const comparableRow = row.length >= 26 ? row.slice(0, defaultRow.length) : row;

  if (JSON.stringify(comparableRow) === JSON.stringify(defaultRow)) {
    return false;
  }

  // Edit page appends blank rows with empty placementDate so they should still be
  // treated as untouched until user edits any field.
  const defaultRowWithEmptyDate = [...defaultRow];
  defaultRowWithEmptyDate[2] = '';

  return JSON.stringify(comparableRow) !== JSON.stringify(defaultRowWithEmptyDate);
};

export const validateTableData = (
  tableData: PlanTableData,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[]
) => {
  const plans: CreatePlanRequest[] = [];
  const rowsWithErrors: RowsWithErrors = [];

  for (let index = 0; index < tableData.length; index++) {
    const row = tableData[index];

    if (!isRowDifferentFromDefault(row, index, plantOptions, witnessCompanyOptions)) {
      continue;
    }

    plans.push(convertFromTableItem(row, plantOptions, witnessCompanyOptions));
    const fieldErrors = validateRowFields(row, plantOptions, witnessCompanyOptions);

    if (fieldErrors.length > 0) {
      rowsWithErrors.push({
        rowIndex: index,
        field: fieldErrors,
      });
    }
  }

  return {
    plans,
    rowsWithErrors,
  };
};

export const mapErrorMessageToColumnIndex = (
  rowsWithErrors: { rowIndex: number; field: { index: number; message: string }[] }[],
  columnIndex: number,
  rowIndex: number,
  message: string
) => {
  // Copy to avoid mutating the original array
  const updatedRowsWithErrors = [...rowsWithErrors];
  // Find row with the same rowIndex
  const rowIndexInArray = updatedRowsWithErrors.findIndex((row) => row.rowIndex === rowIndex);

  if (message) {
    // If there is a message: update or add message
    if (rowIndexInArray !== -1) {
      // Row exists, find field with the same columnIndex
      const fieldIndex = updatedRowsWithErrors[rowIndexInArray].field.findIndex(
        (field) => field.index === columnIndex
      );

      if (fieldIndex !== -1) {
        // Field exists, update message
        updatedRowsWithErrors[rowIndexInArray].field[fieldIndex].message = message;
      } else {
        // Field doesn't exist, add new
        updatedRowsWithErrors[rowIndexInArray].field.push({
          index: columnIndex,
          message: message,
        });
      }
    } else {
      // Row doesn't exist, create new
      updatedRowsWithErrors.push({
        rowIndex: rowIndex,
        field: [
          {
            index: columnIndex,
            message: message,
          },
        ],
      });
    }
  } else if (rowIndexInArray !== -1) {
    // If there is no message: delete field and row if row doesn't have any field
    // Delete field with the same columnIndex
    updatedRowsWithErrors[rowIndexInArray].field = updatedRowsWithErrors[
      rowIndexInArray
    ].field.filter((field) => field.index !== columnIndex);

    // If row doesn't have any field, delete row
    if (updatedRowsWithErrors[rowIndexInArray].field.length === 0) {
      updatedRowsWithErrors.splice(rowIndexInArray, 1);
    }
  }

  return updatedRowsWithErrors;
};

/**
 * Validate dropdown value
 * If the value is not in the source array, reset to default value or blank.
 * Columns with 'values' (e.g. 0=ORDER_STATUS, 10=FORMULATION_PRODUCT_TYPE, 12=VEHICLE_SIZE, 13=PRECEDING_MATERIAL_TYPE)
 * will reset invalid input: to defaultValue when present, otherwise to '' (blank).
 * @param change - Change array
 * @param columnIndex - Column index
 * @param newValue - New value
 */
export const validateDropdownValue = (
  change: CellChange,
  columnIndex: number,
  newValue: unknown
) => {
  const planField = getFieldByColumnIndex(columnIndex);
  if (!planField) return;
  if ('values' in planField && !(planField.values as string[]).includes(newValue as string)) {
    change[3] = 'defaultValue' in planField ? planField.defaultValue : '';
  }
};

/**
 * Normalize full-width input to half-width (digits, colon, etc.) for plan columns that expect half-width values:
 * time (希望/可能), quantity, preceding material quantity.
 * @param change - Change array [row, prop, oldValue, newValue]
 * @param columnIndex - Logical column index (same as `data` / `PLAN_FIELDS.*.id - 1`)
 * @param newValue - Optional; defaults to `change[3]`
 */
export const formatNumberValue = (change: CellChange, columnIndex: number, newValue?: unknown) => {
  const raw = newValue !== undefined ? newValue : change[3];
  if (
    columnIndex === PLAN_FIELDS.TIME_HOPE.id - 1 ||
    columnIndex === PLAN_FIELDS.TIME_POSSIBLE.id - 1 ||
    columnIndex === PLAN_FIELDS.QUANTITY.id - 1 ||
    columnIndex === PLAN_FIELDS.PRECEDING_MATERIAL_QUANTITY.id - 1
  ) {
    change[3] = raw != null && raw !== '' ? convertToHalf(String(raw)) : '';
  }
};
